import React, {useEffect, useState} from 'react';
import {Image, ImageSourcePropType, StyleSheet, Text, View,} from 'react-native';
import {SCREEN_HEIGHT, SCREEN_WIDTH, WINDOW_HEIGHT, WINDOW_WIDTH} from "../../../Config/Constants";
import {BORDER_COLOR, HEADER_TITLE_COLOR, PRIMARY_BACKGROUND_COLOR} from "../../../Config/Colors";
import UpIcon from "../../../Images/Icons/up.png";
import DownIcon from "../../../Images/Icons/down.png";
import DropDownPicker, {ItemType} from 'react-native-dropdown-picker';
import {powerProxyRepository} from "../../../Domain/Repositories/PowerProxyRepository";
import PowerProxy from "../../../Domain/Models/PowerProxy";

interface DropdownProps
{
    /**
     * The default proxy.
     */
    defaultProxy?: PowerProxy;
    
    /**
     * Callback when an item was selected.
     */
    onItemSelected?: (powerProxyId: string) => void;
}

interface DropdownItemType
{
    label: string;
    value: string;
}

/**
 * Renders the Dropdown.
 */
export default function Dropdown({
    defaultProxy,
    onItemSelected,
}: DropdownProps): JSX.Element
{
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(null);
    const [items, setItems] = useState<DropdownItemType[]>([]);
    const [placeholder, setPlaceholder] = useState("");
    
    useEffect(() =>
    {
        initialisePlaceholder();
        setProxies();
    }, []);

    const initialisePlaceholder = (): void =>
    {
        if (defaultProxy === undefined || defaultProxy === null)
        {
            setPlaceholder("Tap to select a proxy...");
            return;
        }

        const initialValue: DropdownItemType = {
            label: defaultProxy.name,
            value: defaultProxy.id,
        };

        setPlaceholder(initialValue.label);
    }
    
    const setProxies = (): void =>
    {
        const dropdownItems: DropdownItemType[] = powerProxyRepository.powerProxies.map(
            (item: PowerProxy) =>
            {
                const dropdownItem: DropdownItemType = {
                    label: item.name,
                    value: item.id,
                }
                
                return dropdownItem;
            });

        const noProxy: DropdownItemType = {
            label: "None",
            value: "",
        } 
        
        dropdownItems.push(noProxy);
        
        setItems(dropdownItems);
    }
    
    const getArrowIconComponent = (source: ImageSourcePropType): JSX.Element =>
    {
        return (
            <Image
                style={styles.arrowIcon}
                source={source}
            />
        ) 
    }
    
    const renderTick = (): JSX.Element =>
    {
        return (
            <View
                style={styles.tickIcon}
            />
        )
    }
    
    const onSelectItem = (item: ItemType<string>): void =>
    {
        if (onItemSelected !== undefined && item.value !== undefined)
        {
            onItemSelected(item.value)
        }
    }
    
    return (
        <View style={styles.parent}>
            <Text style={styles.labelText} >
                Proxy
            </Text>
            
            <View style={styles.dropdownView} >
                <DropDownPicker
                    ArrowUpIconComponent={() => getArrowIconComponent(UpIcon)}
                    ArrowDownIconComponent={() => getArrowIconComponent(DownIcon)}
                    TickIconComponent={renderTick}
                    style={styles.dropdown}
                    containerStyle={styles.container}
                    dropDownContainerStyle={styles.dropdownContainer}
                    selectedItemContainerStyle={styles.selectedItemContainer}
                    placeholder={placeholder}
                    placeholderStyle={styles.placeholder}
                    labelStyle={styles.selectedText}
                    listItemLabelStyle={styles.listItemLabel}
                    arrowIconContainerStyle={styles.arrowIconContainer}
                    tickIconContainerStyle={styles.tickIconContainer}
                    open={open}
                    value={value}
                    items={items}
                    setOpen={setOpen}
                    setValue={setValue}
                    setItems={setItems}
                    onSelectItem={(selectedItem) => onSelectItem(selectedItem)}
                />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    parent: {
        flex: 0.2,
        flexDirection: 'row',
        zIndex: 10,
    },
    labelText: {
        color: HEADER_TITLE_COLOR,
        fontSize: WINDOW_WIDTH * 0.013,
        fontWeight: "600",
        textAlign: 'left',
        marginLeft: '5%',
        alignSelf: 'center',
        flex: 0.3,
    },
    dropdownView: {
        flex: 1,
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 10,
    },
    dropdown: {
        minHeight: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: SCREEN_HEIGHT * 0.002,
        borderColor: BORDER_COLOR,
        borderRadius: SCREEN_WIDTH * 0.005,
        zIndex: 10,
    },
    container: {
        height: '50%',
        width: '93%',
        justifyContent: 'center',
        zIndex: 10,
    },
    dropdownContainer: {
        backgroundColor: PRIMARY_BACKGROUND_COLOR,
        minHeight: WINDOW_HEIGHT * 0.3,
        borderColor: BORDER_COLOR,
        zIndex: 10,
    },
    selectedItemContainer: {
        backgroundColor: BORDER_COLOR,
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        zIndex: 10,
    },
    placeholder: {
        color: HEADER_TITLE_COLOR,
        alignSelf: 'flex-start',
        justifyContent: 'center',
        alignItems: 'center',
        alignContent: 'center',
        paddingTop: '4%',
        textAlign: 'left',
        width: '100%',
        paddingLeft: '5%',
        fontSize: SCREEN_WIDTH * 0.015,
    },
    selectedText: {
        color: HEADER_TITLE_COLOR,
        alignSelf: 'flex-start',
        justifyContent: 'center',
        alignItems: 'center',
        alignContent: 'center',
        paddingTop: '4%',
        textAlign: 'left',
        width: '100%',
        paddingLeft: '5%',
        fontSize: SCREEN_WIDTH * 0.015,
    },
    listItemLabel: {
        paddingVertical: '3%',
        paddingLeft: '5%',
        borderBottomWidth: 2,
        borderBottomColor: BORDER_COLOR,
        fontSize: SCREEN_WIDTH * 0.015,
        color: HEADER_TITLE_COLOR,
    },
    arrowIconContainer: {
        height: '50%',
        width: '10%',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        right: 0,
    },
    tickIconContainer: {
        height: '50%',
        width: '10%',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        right: 0,
    },
    tickIcon: {
        width: '20%',
        height: undefined,
        aspectRatio: 1,
        borderRadius: SCREEN_WIDTH,
        backgroundColor: 'white',
    },
    arrowIcon: {
        width: '40%',
        height: undefined,
        aspectRatio: 1,
    },
});
